import { ErrorBoundary } from 'components/ErrorBoundary'
import Box from '@mui/joy/Box'
import CssBaseline from '@mui/joy/CssBaseline'
import { useColorScheme } from '@mui/joy/styles'
import { useMediaQuery } from '@mui/material'
import { AppThemeProviders } from 'components/shared/AppThemeProviders'
import { MobileNavContext } from 'contexts/MobileNavContext'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import { Children } from 'types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { locationDataSearchV2 } from 'utils/location-search/LocationDataSearchV2'
import { globalDensityStyles } from '../theme/density'
import { NotificationComponent } from 'components/NotificationCenter'
import { Warnings } from 'components/Warnings'
import { useDispatch } from 'react-redux'
import { connecting, disconnected } from 'store/webSocketSlice'
import { SharedWidgets } from './shared-widgets'
import { TeamOnboardingModal } from 'features/onboarding/components/TeamOnboardingModal'
import { AutonomousWakeupDebug } from 'features/agentic-hub/components/AutonomousWakeupDebug'
import { TmsSignupCategoryIntroModal } from '@/pages/tms-onboarding/components/TmsSignupCategoryIntroModal'
import { TmsPageTourOverlay } from '@/pages/tms-onboarding/components/TmsPageTourOverlay'
import { DownloadCenterProvider, DownloadCenterModal } from 'features/download-center'

const SIDEBAR_OPEN_KEY = 'admin-sidebar-open'
/** Routes that open with the sidebar collapsed — wide, column-dense tables that want the full width. */
const SIDEBAR_COLLAPSED_ROUTES = ['/dispatcher/box-truck-loadboard']

function DarkModeSync() {
    const { mode } = useColorScheme()
    useEffect(() => {
        if (mode === 'dark') {
            document.documentElement.classList.add('dark')
        } else {
            document.documentElement.classList.remove('dark')
        }
    }, [mode])
    return null
}

import { useDispatchHelpOperator } from 'features/dispatch-help'
import { useBoxTruckLoadsOperator } from 'features/box-truck-loads'
import { useAuth } from 'hooks/auth'

export default function MasterLayout({ children }: Children) {
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_OPEN_KEY)
        return saved === null ? true : saved === 'true'
    })
    const [showTeamOnboarding, setShowTeamOnboarding] = useState(() => localStorage.getItem('showTeamOnboarding') === 'true')
    // On mobile the sidebar is a slide-in drawer (the burger in the header/pages opens it) rather than a
    // persistent column, so full-screen pages get the whole width. `isMobile` picks column vs drawer.
    const [mobileNavOpen, setMobileNavOpen] = useState(false)
    const isMobile = useMediaQuery('(max-width: 899px)')
    const { pathname } = useLocation()
    // Close the drawer whenever the route changes (tapping a nav item navigates).
    useEffect(() => setMobileNavOpen(false), [pathname])
    const { application } = useAuth()
    // Register this admin as a cross-tenant dispatch-help operator while logged in (heartbeat), so
    // new mobile sessions fan out to it live from any page. For now every admin; Flipt-gate later.
    useDispatchHelpOperator(!!application)
    // Resolve whether this account may see the gated Box Truck Loads sidebar section (Gluon allowlist).
    useBoxTruckLoadsOperator(!!application)
    // The Help Center is a self-contained, distraction-free page — hide warning banners on it.
    const isHelpCenter = pathname.startsWith('/help-center')

    const handleCloseTeamOnboarding = useCallback(() => {
        setShowTeamOnboarding(false)
        localStorage.removeItem('showTeamOnboarding')
    }, [])

    const dispatch = useDispatch()

    // Initialize WebSocket connection when MasterLayout mounts and handle disconnection on unmount
    useEffect(() => {
        // Attempt to connect when component mounts
        dispatch(connecting())

        // Disconnect when component unmounts
        return () => {
            dispatch(disconnected('MasterLayout unmounted'))
        }
    }, [dispatch])

    const toggleSidebar = () => {
        setSidebarOpen((prev) => {
            const next = !prev
            localStorage.setItem(SIDEBAR_OPEN_KEY, String(next))
            return next
        })
    }

    /**
     * Collapse the sidebar on the routes that want the full width (the Box Truck loadboard's grid).
     *
     * Two things this deliberately does NOT do. It never writes SIDEBAR_OPEN_KEY: this is a per-route
     * default, not a change to the operator's global preference, so leaving the route restores whatever
     * they had everywhere else. And it only fires when the route CROSSES the boundary — an operator who
     * opens the sidebar while on the board keeps it open, instead of having it slam shut on the next
     * render or on navigating within the page.
     */
    const wantsCollapsed = SIDEBAR_COLLAPSED_ROUTES.some((route) => pathname.startsWith(route))
    // `null` until the first run so landing directly on one of these routes is handled like entering it.
    const previousWantsCollapsed = useRef<boolean | null>(null)
    useEffect(() => {
        if (previousWantsCollapsed.current === wantsCollapsed) return
        previousWantsCollapsed.current = wantsCollapsed
        setSidebarOpen(wantsCollapsed ? false : localStorage.getItem(SIDEBAR_OPEN_KEY) !== 'false')
    }, [wantsCollapsed])

    // Keep the `--Sidebar-width` CSS variable in sync with the toggle state.
    //
    // NOTE: this effect previously had a buddy effect with NO dependency array,
    // causing it to fire on every render — including every route change. Writing
    // to `document.documentElement.style` on every render invalidated the browser's
    // style cache repeatedly and contributed to the zoom-in/out flicker visible
    // when menu items were clicked rapidly. A single dep-tracked effect is enough.
    useEffect(() => {
        // Kept in sync with the inline `--Sidebar-width` in Sidebar.tsx and the reference
        // design's 220px open / 56px collapsed footprint.
        document.documentElement.style.setProperty('--Sidebar-width', sidebarOpen ? '220px' : '56px')
    }, [sidebarOpen])

    useEffect(() => {
        // Prewarm the location database
        locationDataSearchV2.initialize()
    }, [])

    return (
        <AppThemeProviders>
            <DownloadCenterProvider>
                <DarkModeSync />
                <CssBaseline />
                <MobileNavContext.Provider value={{ openMobileNav: () => setMobileNavOpen(true) }}>
                    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
                        <NotificationComponent />
                        <Header />
                        {isMobile ? (
                            <>
                                {mobileNavOpen && <div className="fixed inset-0 z-[9996] bg-slate-900/50" onClick={() => setMobileNavOpen(false)} />}
                                <div
                                    className={`fixed left-0 top-0 z-[9997] h-dvh shadow-2xl transition-transform duration-300 ${
                                        mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
                                    }`}
                                >
                                    <Sidebar isOpen onToggle={() => setMobileNavOpen(false)} />
                                </div>
                            </>
                        ) : (
                            <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
                        )}
                        <Box
                            component="main"
                            className="MainContent"
                            sx={{
                                pl: 0,
                                pt: {
                                    xs: 'calc(12px + var(--Header-height))',
                                    sm: 'calc(12px + var(--Header-height))',
                                    md: 0,
                                },
                                pb: 0,
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                                height: { xs: '100vh', md: '100vh' },
                                gap: 1,
                                transition: 'padding-left 0.4s',
                                ...globalDensityStyles,
                                overflow: 'auto',
                            }}
                        >
                            {!isHelpCenter && <Warnings />}

                            {/*
                    <Typography level="h3" sx={{ textTransform: 'capitalize', pl: 1 }}>
                        {pathname ? pathname?.pop()?.replace(/-/g, ' ') : 'Dashboard'}
                    </Typography>
                    */}

                            <ErrorBoundary resetKeys={[pathname]}>{children}</ErrorBoundary>
                        </Box>

                        <SharedWidgets />
                        <AutonomousWakeupDebug />
                        <TmsSignupCategoryIntroModal />
                        <TmsPageTourOverlay />
                        <TeamOnboardingModal open={showTeamOnboarding} onClose={handleCloseTeamOnboarding} />
                        <DownloadCenterModal />
                    </Box>
                </MobileNavContext.Provider>
            </DownloadCenterProvider>
        </AppThemeProviders>
    )
}
